<template>
  <div class="playlist-list">
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <el-col :span="8">
        <el-input
          v-model="searchQuery"
          placeholder="请输入歌单名称关键字"
          clearable
          @keyup.enter="handleSearch"
        >
          <template #append>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </template>
        </el-input>
      </el-col>
      <el-col :span="4">
        <el-button type="success" @click="openAddDialog">新增歌单</el-button>
      </el-col>
    </el-row>
    <el-table
      :data="playlists"
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa' }"
    >
      <el-table-column prop="id" label="ID" min-width="100" show-overflow-tooltip />
      <el-table-column prop="name" label="歌单名称" min-width="150" show-overflow-tooltip />
      <el-table-column prop="owner_name" label="所有者" min-width="120" show-overflow-tooltip>
        <template #default="scope">
          <div style="display: flex; align-items: center; gap: 8px;">
            <el-tag :type="scope.row.owner_id === currentUser.id ? 'success' : 'info'" size="small">
              {{ scope.row.owner_id === 1 || scope.row.owner_name === 'admin' ? '平台' : (scope.row.owner_name || '未知用户') }}
            </el-tag>
            <el-tag 
              v-if="scope.row.owner_id === currentUser.id" 
              type="success" 
              size="small"
              effect="plain"
            >
              我的
            </el-tag>
            <el-tag 
              v-else-if="authorizedPlaylistIds.includes(scope.row.id)" 
              type="warning" 
              size="small"
              effect="plain"
            >
              已授权
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="is_public" label="公开" width="70">
        <template #default="scope">
          <el-tag :type="scope.row.is_public ? 'success' : 'info'" size="small">
            {{ scope.row.is_public ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="category_name" label="类别" width="100" show-overflow-tooltip />
      <el-table-column prop="tags" label="标签" min-width="120">
        <template #default="scope">
          <template v-if="scope.row.tags && scope.row.tags.length">
            <el-tag v-for="t in scope.row.tags" :key="t.id" size="small" style="margin-right: 4px;">{{ t.name }}</el-tag>
          </template>
          <span v-else class="text-muted">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" min-width="150" show-overflow-tooltip />
      <el-table-column prop="weight" label="权重" width="70" />
      <el-table-column prop="is_default" label="默认" width="70">
        <template #default="scope">
          <el-tag :type="scope.row.is_default ? 'success' : 'info'">
            {{ scope.row.is_default ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="140" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="scope">
          <el-button size="small" type="primary" @click="onViewSongs(scope.row)">查看</el-button>
          <!-- 只有管理员或歌单所有者可以编辑 -->
          <el-button 
            size="small"
            type="primary"
            @click="onEdit(scope.row)"
            v-if="canEditPlaylist(scope.row)"
          >编辑</el-button>
          <!-- 授权按钮：管理员可以授权任何歌单，普通用户可以授权自己的歌单 -->
          <el-button 
            size="small" 
            type="warning" 
            @click="onGrantAccess(scope.row)"
            v-if="canGrantPlaylist()"
          >授权</el-button>
          <!-- 只有管理员或歌单所有者可以删除 -->
          <el-button 
            size="small" 
            type="danger" 
            @click="onDelete(scope.row)"
            v-if="canDeletePlaylist(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      style="margin-top: 20px; text-align: right;"
      background
      layout="prev, pager, next, jumper, ->, total"
      :total="total"
      :page-size="pageSize"
      v-model:current-page="currentPage"
      @current-change="handlePageChange"
    />

    <!-- 新增/编辑歌单弹窗 -->
    <el-dialog :title="editMode ? '编辑歌单' : '新增歌单'" v-model="showEditDialog" width="800px">
      <el-form :model="editForm" label-width="80px">
        <el-form-item label="ID">
          <el-input v-model="editForm.id" :disabled="editMode" />
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="editForm.name" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="editForm.description" />
        </el-form-item>
        <el-form-item label="类别" required>
          <el-select v-model="editForm.category_id" placeholder="请选择类别" style="width: 100%;">
            <el-option
              v-for="c in playlistCategories"
              :key="c.id"
              :label="c.name"
              :value="c.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="editForm.tag_ids"
            multiple
            filterable
            allow-create
            placeholder="最多选 5 个标签，可输入新建"
            style="width: 100%;"
            :max-collapse-tags="2"
          >
            <el-option v-for="t in tagOptions" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
          <span class="form-tip">最多 5 个标签</span>
        </el-form-item>
        <el-form-item label="权重">
          <el-input-number v-model="editForm.weight" :min="0" :max="10" :step="1" />
        </el-form-item>
        <el-form-item label="默认歌单">
          <el-switch v-model="editForm.is_default" />
        </el-form-item>
        <el-form-item label="是否公开">
          <el-switch v-model="editForm.is_public" />
          <span style="margin-left: 10px; color: #909399; font-size: 12px;">
            公开的歌单所有设备都可以访问
          </span>
        </el-form-item>
        
        <!-- 歌曲选择区域 -->
        <el-form-item label="歌曲">
          <div class="song-selection">
            <!-- 歌曲搜索 -->
            <div class="song-search">
              <el-input
                v-model="songSearchQuery"
                placeholder="搜索歌曲名称或艺术家"
                clearable
                @input="filterSongs"
                style="margin-bottom: 10px;"
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
              
              <!-- 可选歌曲列表 -->
              <div class="available-songs" v-if="filteredSongs.length > 0">
                <div class="song-list-title">可选歌曲：</div>
                <div class="song-list">
                  <div
                    v-for="song in filteredSongs"
                    :key="song.id"
                    class="song-item"
                    @click="addSongToPlaylist(song)"
                  >
                    <span class="song-title">{{ song.title }}</span>
                    <span class="song-artist">- {{ song.artist }}</span>
                  </div>
                </div>
              </div>
              <div v-else-if="songSearchQuery" class="no-songs">
                未找到匹配的歌曲
              </div>
            </div>
            
            <!-- 已选歌曲列表 -->
            <div class="selected-songs" v-if="selectedSongs.length > 0">
              <div class="song-list-title">已选歌曲：</div>
              <div class="song-list">
                <div
                  v-for="(song, index) in selectedSongs"
                  :key="song.id"
                  class="song-item selected"
                >
                  <span class="song-title">{{ song.title }}</span>
                  <span class="song-artist">- {{ song.artist }}</span>
                  <el-button
                    type="danger"
                    size="small"
                    circle
                    @click="removeSongFromPlaylist(index)"
                    style="margin-left: 10px;"
                  >
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>

    <!-- 新增歌单弹窗 -->
    <el-dialog title="新增歌单" v-model="showAddDialog" width="800px">
      <el-form :model="addForm" label-width="80px">
        <el-form-item label="名称">
          <el-input v-model="addForm.name" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="addForm.description" />
        </el-form-item>
        <el-form-item label="类别" required>
          <el-select v-model="addForm.category_id" placeholder="请选择类别" style="width: 100%;">
            <el-option
              v-for="c in playlistCategories"
              :key="c.id"
              :label="c.name"
              :value="c.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="addForm.tag_ids"
            multiple
            filterable
            allow-create
            placeholder="最多选 5 个标签，可输入新建"
            style="width: 100%;"
            :max-collapse-tags="2"
          >
            <el-option v-for="t in tagOptions" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
          <span class="form-tip">最多 5 个标签</span>
        </el-form-item>
        <el-form-item label="权重">
          <el-input-number v-model="addForm.weight" :min="0" :max="10" :step="1" />
        </el-form-item>
        <el-form-item label="默认歌单">
          <el-switch v-model="addForm.is_default" />
        </el-form-item>
        <el-form-item label="是否公开">
          <el-switch v-model="addForm.is_public" />
          <span style="margin-left: 10px; color: #909399; font-size: 12px;">
            公开的歌单所有设备都可以访问
          </span>
        </el-form-item>
        
        <!-- 歌曲选择区域 -->
        <el-form-item label="歌曲">
          <div class="song-selection">
            <!-- 歌曲搜索 -->
            <div class="song-search">
              <el-input
                v-model="addSongSearchQuery"
                placeholder="搜索歌曲名称或艺术家"
                clearable
                @input="filterAddSongs"
                style="margin-bottom: 10px;"
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
              
              <!-- 可选歌曲列表 -->
              <div class="available-songs" v-if="filteredAddSongs.length > 0">
                <div class="song-list-title">可选歌曲：</div>
                <div class="song-list">
                  <div
                    v-for="song in filteredAddSongs"
                    :key="song.id"
                    class="song-item"
                    @click="addSongToAddForm(song)"
                  >
                    <span class="song-title">{{ song.title }}</span>
                    <span class="song-artist">- {{ song.artist }}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 已选歌曲列表 -->
            <div class="selected-songs" v-if="addSelectedSongs.length > 0">
              <div class="song-list-title">已选歌曲：</div>
              <div class="song-list">
                <div
                  v-for="(song, index) in addSelectedSongs"
                  :key="song.id"
                  class="song-item selected"
                >
                  <span class="song-title">{{ song.title }}</span>
                  <span class="song-artist">- {{ song.artist }}</span>
                  <el-button
                    type="danger"
                    size="small"
                    circle
                    @click="removeSongFromAddForm(index)"
                    style="margin-left: 10px;"
                  >
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showAddDialog = false">取消</el-button>
        <el-button type="primary" @click="handleAdd">新增</el-button>
      </template>
    </el-dialog>

    <!-- 授权弹窗 -->
    <el-dialog title="授权歌单给用户账号" v-model="showGrantDialog" width="600px">
      <div v-if="currentGrantPlaylist">
        <p><strong>歌单名称：</strong>{{ currentGrantPlaylist.name }}</p>
        <p><strong>歌单ID：</strong>{{ currentGrantPlaylist.id }}</p>
        <el-divider />
        
        <el-form :model="grantForm" label-width="100px">
          <el-form-item label="选择用户" required>
            <el-select
              v-model="grantForm.user_id"
              placeholder="请选择要授权的用户"
              filterable
              style="width: 100%"
            >
              <el-option
                v-for="user in availableUsers"
                :key="user.id"
                :label="`${user.username} (${user.role === 'admin' ? '管理员' : '普通用户'})`"
                :value="user.id"
              />
            </el-select>
          </el-form-item>
        </el-form>
        
        <!-- 已授权用户列表 -->
        <el-divider />
        <div v-if="grantedUsers.length > 0">
          <p><strong>已授权用户：</strong></p>
          <el-table :data="grantedUsers" size="small" max-height="200">
            <el-table-column prop="username" label="用户名" />
            <el-table-column prop="role" label="角色">
              <template #default="scope">
                <el-tag :type="scope.row.role === 'admin' ? 'danger' : 'info'">
                  {{ scope.row.role === 'admin' ? '管理员' : '普通用户' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="授权时间" />
            <el-table-column label="操作" width="100">
              <template #default="scope">
                <el-button
                  size="small"
                  type="danger"
                  @click="onRevokeAccess(scope.row.access_id)"
                  v-if="canRevokeAccess(scope.row)"
                >取消</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else>
          <el-empty description="暂无授权用户" :image-size="80" />
        </div>
      </div>
      <template #footer>
        <el-button @click="showGrantDialog = false">关闭</el-button>
        <el-button 
          type="primary" 
          @click="handleGrantAccess"
          :disabled="!grantForm.user_id"
        >确认授权</el-button>
        <el-button 
          type="success" 
          @click="handleGrantToAll"
          v-if="currentUser.role === 'admin'"
        >授权给所有用户</el-button>
      </template>
    </el-dialog>

    <!-- 查看歌曲弹窗 -->
    <el-dialog :title="`歌单歌曲 - ${currentPlaylist?.name || ''}`" v-model="showViewSongsDialog" width="600px">
      <div v-if="currentPlaylist">
        <div class="playlist-info">
          <p><strong>歌单ID：</strong>{{ currentPlaylist.id }}</p>
          <p><strong>歌单名称：</strong>{{ currentPlaylist.name }}</p>
          <p><strong>描述：</strong>{{ currentPlaylist.description || '无' }}</p>
          <p><strong>权重：</strong>{{ currentPlaylist.weight }}</p>
          <p><strong>默认歌单：</strong>
            <el-tag :type="currentPlaylist.is_default ? 'success' : 'info'">
              {{ currentPlaylist.is_default ? '是' : '否' }}
            </el-tag>
          </p>
          <p><strong>创建时间：</strong>{{ currentPlaylist.created_at || '未知' }}</p>
          <p><strong>歌曲数量：</strong>{{ playlistSongs.length }} 首</p>
        </div>
        
        <div v-if="playlistSongs.length > 0" class="playlist-songs">
          <h4>歌曲列表：</h4>
          <div class="song-list">
            <div
              v-for="(song, index) in playlistSongs"
              :key="song.id"
              class="song-item"
            >
              <span class="song-index">{{ index + 1 }}.</span>
              <span class="song-title">{{ song.title }}</span>
              <span class="song-artist">- {{ song.artist }}</span>
            </div>
          </div>
        </div>
        <div v-else class="no-songs">
          <el-empty description="该歌单暂无歌曲" />
        </div>
      </div>
      <template #footer>
        <el-button @click="showViewSongsDialog = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getPlaylists,
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  getPlaylistAccountAccess,
  grantPlaylistToAccount,
  revokePlaylistAccountAccess,
  grantPlaylistToAllAccounts,
  getPlaylistCategories,
  getTags,
} from '../api/playlist';
import { getSongs } from '../api/song';
import { getUsers } from '../api/auth';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Delete } from '@element-plus/icons-vue';

export default {
  name: 'PlaylistList',
  components: {
    Search,
    Delete,
  },
  data() {
    return {
      playlists: [],
      total: 0,
      loading: false,
      searchQuery: '',
      currentPage: 1,
      pageSize: 10,
      showEditDialog: false,
      showAddDialog: false,
      editMode: false,
      editForm: {
        id: '',
        name: '',
        description: '',
        category_id: null,
        tag_ids: [],
        weight: 5,
        is_default: false,
        is_public: false,
        song_ids: [],
      },
      addForm: {
        name: '',
        description: '',
        category_id: null,
        tag_ids: [],
        weight: 5,
        is_default: false,
        is_public: false,
        song_ids: [],
      },
      playlistCategories: [],
      tagOptions: [],
      // 歌曲相关数据
      allSongs: [],
      songSearchQuery: '',
      addSongSearchQuery: '',
      selectedSongs: [],
      addSelectedSongs: [],
      // 查看歌曲相关数据
      showViewSongsDialog: false,
      currentPlaylist: null,
      playlistSongs: [],
      // 授权相关数据
      showGrantDialog: false,
      currentGrantPlaylist: null,
      grantForm: {
        user_id: null,
      },
      availableUsers: [],
      grantedUsers: [],
      // 已授权的歌单ID列表（用于标记）
      authorizedPlaylistIds: [],
      // 当前用户信息
      currentUser: {
        username: '',
        role: '',
        id: null
      },
    };
  },
  computed: {
    filteredSongs() {
      if (!this.songSearchQuery) {
        return this.allSongs.filter(song => 
          !this.selectedSongs.some(selected => selected.id === song.id)
        );
      }
      const query = this.songSearchQuery.toLowerCase();
      return this.allSongs.filter(song => 
        !this.selectedSongs.some(selected => selected.id === song.id) &&
        (song.title?.toLowerCase().includes(query) || 
         song.artist?.toLowerCase().includes(query))
      );
    },
    filteredAddSongs() {
      if (!this.addSongSearchQuery) {
        return this.allSongs.filter(song => 
          !this.addSelectedSongs.some(selected => selected.id === song.id)
        );
      }
      const query = this.addSongSearchQuery.toLowerCase();
      return this.allSongs.filter(song => 
        !this.addSelectedSongs.some(selected => selected.id === song.id) &&
        (song.title?.toLowerCase().includes(query) || 
         song.artist?.toLowerCase().includes(query))
      );
    },
  },
  methods: {
    // 权限检查方法
    canEditPlaylist(playlist) {
      // 管理员可以编辑所有歌单
      if (this.currentUser.role === 'admin') {
        return true;
      }
      // 普通用户只能编辑自己的歌单
      return playlist.owner_id === this.currentUser.id;
    },
    
    canDeletePlaylist(playlist) {
      // 管理员可以删除所有歌单
      if (this.currentUser.role === 'admin') {
        return true;
      }
      // 普通用户只能删除自己的歌单
      return playlist.owner_id === this.currentUser.id;
    },
    
    canGrantPlaylist() {
      // 只有管理员才需要「授权」功能，普通用户本来就能访问自己的歌单，不再展示授权按钮
      return this.currentUser.role === 'admin';
    },
    
    canRevokeAccess(grantedUser) {
      // 管理员可以取消任何授权
      if (this.currentUser.role === 'admin') {
        return true;
      }
      // 普通用户只能取消自己账号的授权
      return grantedUser.user_id === this.currentUser.id;
    },
    
    // 获取当前用户信息
    getCurrentUser() {
      const userStr = localStorage.getItem('user');
      let user = {};
      try {
        if (userStr && userStr !== 'undefined') {
          user = JSON.parse(userStr);
        }
      } catch (e) {
        user = {};
      }
      this.currentUser = {
        username: user.username,
        role: user.role,
        id: user.id || null
      };
    },
    
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          query: this.searchQuery,
          page: this.currentPage,
          page_size: this.pageSize,
        };
        const res = await getPlaylists(params);
        // 注意：响应拦截器已经返回了 response.data，所以 res 直接就是后端返回的 JSON 对象
        if (res && res.success) {
          this.playlists = res.data || [];
          this.total = res.total || this.playlists.length;
          
          // 加载已授权的歌单ID列表（用于标记）
          if (this.currentUser.role !== 'admin') {
            await this.loadAuthorizedPlaylistIds();
          }
        } else {
          ElMessage.error((res && res.message) || '获取歌单失败');
        }
      } catch (e) {
        ElMessage.error('请求失败: ' + e);
      } finally {
        this.loading = false;
      }
    },
    
    async loadAuthorizedPlaylistIds() {
      try {
        const res = await getPlaylistAccountAccess();
        if (res && res.success) {
          // 提取已授权的歌单ID列表
          this.authorizedPlaylistIds = res.data.map(item => item.playlist_id);
        }
      } catch (e) {
        console.error('加载已授权歌单失败:', e);
      }
    },
    
    async fetchSongs() {
      try {
        // 从后端获取歌曲列表，用于在歌单中选择歌曲
        const params = {
          page: 1,
          page_size: 1000, // 一次拉取足够多的歌曲供选择
        };
        const res = await getSongs(params);
        if (res && res.success) {
          this.allSongs = res.data || [];
        } else {
          ElMessage.error((res && res.message) || '获取歌曲列表失败');
        }
      } catch (e) {
        ElMessage.error('获取歌曲列表失败: ' + e);
      }
    },
    
    handleSearch() {
      this.currentPage = 1;
      this.fetchData();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchData();
    },
    
    onEdit(row) {
      // 检查编辑权限
      if (!this.canEditPlaylist(row)) {
        ElMessage.warning('您没有权限编辑此歌单');
        return;
      }
      
      this.editMode = true;
      // 深拷贝并确保 is_default、is_public 为布尔；类别与标签
      this.editForm = { 
        ...row,
        category_id: row.category_id != null ? row.category_id : null,
        tag_ids: (row.tags || []).map(t => t.id),
        is_default: Boolean(row.is_default === 1 || row.is_default === true),
        is_public: Boolean(row.is_public === 1 || row.is_public === true)
      };
      // 根据song_ids获取歌曲对象
      this.selectedSongs = this.allSongs.filter(song => 
        row.song_ids && row.song_ids.includes(song.id)
      );
      this.songSearchQuery = '';
      this.showEditDialog = true;
    },
    
    async handleSave() {
      try {
        // 验证必填字段
        if (!this.editForm.name || this.editForm.name.trim() === '') {
          ElMessage.error('歌单名称不能为空');
          return;
        }
        if (this.editForm.category_id == null || this.editForm.category_id === '') {
          ElMessage.error('请选择类别');
          return;
        }
        const tagVals = this.editForm.tag_ids || [];
        if (tagVals.length > 5) {
          ElMessage.error('最多 5 个标签');
          return;
        }
        
        // 更新song_ids
        this.editForm.song_ids = this.selectedSongs.map(song => song.id);
        
        const tag_ids = tagVals.filter(v => typeof v === 'number');
        const tag_names = tagVals.filter(v => typeof v === 'string' && String(v).trim());
        
        const requestData = {
          ...this.editForm,
          category_id: Number(this.editForm.category_id),
          tag_ids,
          tag_names,
          is_default: Boolean(this.editForm.is_default),
          is_public: Boolean(this.editForm.is_public)
        };
        
        const res = await updatePlaylist(this.editForm.id, requestData);
        console.log('保存响应:', res); // 调试日志
        
        // 注意：响应拦截器已经返回了 response.data，所以 res 直接就是后端返回的 JSON 对象
        if (res && res.success) {
          ElMessage.success(res.message || '保存成功');
          this.showEditDialog = false;
          // 强制刷新数据，确保显示最新状态
          this.currentPage = 1; // 重置到第一页，确保能看到更新的数据
          await this.fetchData();
        } else {
          const errorMsg = (res && res.message) || '保存失败';
          console.error('保存失败响应:', res); // 调试日志
          ElMessage.error(errorMsg);
        }
      } catch (e) {
        console.error('保存失败异常:', e);
        ElMessage.error('请求失败: ' + (e.message || e.toString()));
      }
    },
    
    onDelete(row) {
      // 检查删除权限
      if (!this.canDeletePlaylist(row)) {
        ElMessage.warning('您没有权限删除此歌单');
        return;
      }
      
      ElMessageBox.confirm(`确定要删除歌单"${row.name}"吗？`, '提示', {
        type: 'warning',
      })
        .then(async () => {
          const res = await deletePlaylist(row.id);
          // 注意：响应拦截器已经返回了 response.data，所以 res 直接就是后端返回的 JSON 对象
          if (res && res.success) {
            ElMessage.success(res.message || '删除成功');
            this.fetchData();
          } else {
            ElMessage.error((res && res.message) || '删除失败');
          }
        })
        .catch(() => {});
    },
    
    openAddDialog() {
      // 重置表单（不包含 id，后端会自动生成）
      this.addForm = {
        name: '',
        description: '',
        category_id: null,
        tag_ids: [],
        weight: 5,
        is_default: false,
        is_public: false,
        song_ids: [],
      };
      this.addSelectedSongs = [];
      this.addSongSearchQuery = '';
      this.showAddDialog = true;
    },
    
    handleAdd() {
      if (!this.addForm.category_id && this.addForm.category_id !== 0) {
        ElMessage.error('请选择类别');
        return;
      }
      const tagVals = this.addForm.tag_ids || [];
      if (tagVals.length > 5) {
        ElMessage.error('最多 5 个标签');
        return;
      }
      // 更新song_ids
      this.addForm.song_ids = this.addSelectedSongs.map(song => song.id);
      
      const tag_ids = tagVals.filter(v => typeof v === 'number');
      const tag_names = tagVals.filter(v => typeof v === 'string' && String(v).trim());
      
      // 创建请求数据，不包含 id 字段（后端会自动生成）
      const requestData = {
        ...this.addForm,
        category_id: Number(this.addForm.category_id),
        tag_ids,
        tag_names,
      };
      delete requestData.id;
      
      createPlaylist(requestData)
        .then((res) => {
          // 注意：响应拦截器已经返回了 response.data，所以 res 直接就是后端返回的 JSON 对象
          if (res && res.success) {
            ElMessage.success(res.message || '新增成功');
            // 成功时：关闭弹窗，重置表单，刷新数据
            this.showAddDialog = false;
            this.addForm = {
              name: '',
              description: '',
              category_id: null,
              tag_ids: [],
              weight: 5,
              is_default: false,
              is_public: false,
              song_ids: [],
            };
            this.addSelectedSongs = [];
            this.addSongSearchQuery = '';
            this.fetchData();
          } else {
            // 失败时：保留弹窗，只显示错误消息
            ElMessage.error((res && res.message) || '新增失败');
          }
        })
        .catch((e) => {
          // 失败时：保留弹窗，只显示错误消息
          ElMessage.error('请求失败: ' + e);
        });
    },
    
    // 歌曲选择相关方法
    addSongToPlaylist(song) {
      if (!this.selectedSongs.some(s => s.id === song.id)) {
        this.selectedSongs.push(song);
      }
    },
    
    removeSongFromPlaylist(index) {
      this.selectedSongs.splice(index, 1);
    },
    
    addSongToAddForm(song) {
      if (!this.addSelectedSongs.some(s => s.id === song.id)) {
        this.addSelectedSongs.push(song);
      }
    },
    
    removeSongFromAddForm(index) {
      this.addSelectedSongs.splice(index, 1);
    },
    
    filterSongs() {
      // 计算属性会自动处理过滤
    },
    
    filterAddSongs() {
      // 计算属性会自动处理过滤
    },
    
    onViewSongs(row) {
      this.currentPlaylist = row;
      this.playlistSongs = this.allSongs.filter(song => 
        row.song_ids && row.song_ids.includes(song.id)
      );
      this.showViewSongsDialog = true;
    },
    
    async onGrantAccess(row) {
      // 检查授权权限
      if (!this.canGrantPlaylist(row)) {
        ElMessage.warning('您没有权限授权此歌单');
        return;
      }
      
      this.currentGrantPlaylist = row;
      this.grantForm.user_id = null;
      this.showGrantDialog = true;
      
      // 加载用户列表和已授权用户
      await this.loadUsers();
      await this.loadGrantedUsers(row.id);
    },
    
    async loadUsers() {
      try {
        const res = await getUsers({ page: 1, page_size: 1000 });
        if (res && res.success) {
          // 如果是普通用户，只能看到自己
          if (this.currentUser.role !== 'admin') {
            this.availableUsers = res.data.filter(user => user.id === this.currentUser.id);
          } else {
            this.availableUsers = res.data || [];
          }
        }
      } catch (e) {
        console.error('加载用户列表失败:', e);
        ElMessage.error('加载用户列表失败');
      }
    },
    
    async loadGrantedUsers(playlistId) {
      try {
        const res = await getPlaylistAccountAccess();
        if (res && res.success) {
          // 过滤出当前歌单的授权记录
          const granted = res.data.filter(item => item.playlist_id === playlistId);
          
          // 获取用户信息
          const userRes = await getUsers({ page: 1, page_size: 1000 });
          const allUsers = userRes && userRes.success ? (userRes.data || []) : [];
          
          this.grantedUsers = granted.map(item => {
            const user = allUsers.find(u => u.id === item.user_id);
            return {
              access_id: item.id,
              user_id: item.user_id,
              username: user ? user.username : `用户${item.user_id}`,
              role: user ? user.role : 'user',
              created_at: item.created_at
            };
          });
        }
      } catch (e) {
        console.error('加载已授权用户失败:', e);
        ElMessage.error('加载已授权用户失败');
      }
    },
    
    async handleGrantAccess() {
      if (!this.grantForm.user_id) {
        ElMessage.warning('请选择要授权的用户');
        return;
      }
      
      try {
        const res = await grantPlaylistToAccount({
          playlist_id: this.currentGrantPlaylist.id,
          user_id: this.grantForm.user_id
        });
        
        if (res && res.success) {
          ElMessage.success(res.message || '授权成功');
          this.grantForm.user_id = null;
          // 重新加载已授权用户列表
          await this.loadGrantedUsers(this.currentGrantPlaylist.id);
        } else {
          ElMessage.error((res && res.message) || '授权失败');
        }
      } catch (e) {
        console.error('授权失败:', e);
        ElMessage.error('授权失败: ' + (e.message || e.toString()));
      }
    },
    
    async handleGrantToAll() {
      if (this.currentUser.role !== 'admin') {
        ElMessage.warning('只有客服人员可以批量授权');
        return;
      }
      
      try {
        await ElMessageBox.confirm(
          `确定要将歌单"${this.currentGrantPlaylist.name}"授权给所有用户吗？`,
          '批量授权确认',
          {
            type: 'warning',
            confirmButtonText: '确定',
            cancelButtonText: '取消'
          }
        );
        
        const res = await grantPlaylistToAllAccounts(this.currentGrantPlaylist.id);
        
        if (res && res.success) {
          ElMessage.success(res.message || '批量授权成功');
          // 重新加载已授权用户列表
          await this.loadGrantedUsers(this.currentGrantPlaylist.id);
        } else {
          ElMessage.error((res && res.message) || '批量授权失败');
        }
      } catch (e) {
        if (e !== 'cancel') {
          console.error('批量授权失败:', e);
          ElMessage.error('批量授权失败: ' + (e.message || e.toString()));
        }
      }
    },
    
    async onRevokeAccess(accessId) {
      try {
        await ElMessageBox.confirm('确定要取消此授权吗？', '取消授权确认', {
          type: 'warning',
          confirmButtonText: '确定',
          cancelButtonText: '取消'
        });
        
        const res = await revokePlaylistAccountAccess(accessId);
        
        if (res && res.success) {
          ElMessage.success(res.message || '取消授权成功');
          // 重新加载已授权用户列表
          await this.loadGrantedUsers(this.currentGrantPlaylist.id);
        } else {
          ElMessage.error((res && res.message) || '取消授权失败');
        }
      } catch (e) {
        if (e !== 'cancel') {
          console.error('取消授权失败:', e);
          ElMessage.error('取消授权失败: ' + (e.message || e.toString()));
        }
      }
    },
    async loadPlaylistMeta() {
      try {
        const [catRes, tagRes] = await Promise.all([
          getPlaylistCategories(),
          getTags(),
        ]);
        if (catRes && catRes.success && catRes.data) {
          this.playlistCategories = catRes.data;
        }
        if (tagRes && tagRes.success && tagRes.data) {
          this.tagOptions = tagRes.data;
        }
      } catch (e) {
        console.error('加载类别/标签失败', e);
      }
    },
  },
  mounted() {
    this.getCurrentUser();
    this.loadPlaylistMeta();
    this.fetchData();
    this.fetchSongs();
  },
};
</script>

<style scoped>
.playlist-list {
  padding: 24px;
}

.song-selection {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 16px;
  background-color: #fafafa;
}

.song-search {
  margin-bottom: 16px;
}

.song-list-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #606266;
}

.song-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: white;
}

.song-item {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.song-item:hover {
  background-color: #f5f7fa;
}

.song-item.selected {
  background-color: #e1f3d8;
  border-left: 3px solid #67c23a;
}

.song-item:last-child {
  border-bottom: none;
}

.song-title {
  font-weight: 500;
  color: #303133;
}

.song-artist {
  color: #909399;
  font-size: 12px;
}

.no-songs {
  text-align: center;
  color: #909399;
  padding: 20px;
}

.available-songs {
  margin-bottom: 16px;
}

.selected-songs {
  border-top: 1px solid #e4e7ed;
  padding-top: 16px;
}

.playlist-info {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.playlist-info p {
  margin: 8px 0;
  line-height: 1.5;
}

.playlist-songs h4 {
  margin-bottom: 10px;
  color: #303133;
  font-size: 16px;
}

.song-index {
  margin-right: 10px;
  color: #909399;
  font-size: 14px;
  min-width: 30px;
  display: inline-block;
}

.form-tip {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.text-muted {
  color: #909399;
}
</style>
